/* eslint-disable @stylistic/brace-style */
import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        /* max-width: 1024px; */
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-block: 40px;
        /* display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(35vh + 10px) minmax(calc(35vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px; */
    }

    .title {
        text-align: center;
        overflow: auto;
    }

    .main-screen-parent {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(12, minmax(0, 1fr));
    }
    
    .main-screen {
        grid-column: span 8 / span 8;
        margin-right: 40px;
        margin-left: 40px;
        position: relative;
    }

    .inventory, .journal {
        display: flex;
        flex-direction: column;
        align-items: center;
        grid-column: span 2 / span 2;
    }

    .inventory .journal, .title {
        margin-bottom: 10px;
    }

    .main-screen .image-parent .character-img {
        position: absolute;
        width: 400px;
        height: 400px;
        bottom: 0;
        right: 0;
        -webkit-transform: scaleX(-1);
        transform: scaleX(-1);
    }

    .inventory {
        flex-direction: column;
        align-self: start;
        justify-content: center;
        justify-self: center;
        align-items: center;
        gap: 10px;
    }

    inventory-items {
        /* display: grid;
        grid-template-columns: repeat(2, 70px);
        gap: 10px; */
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        max-width: calc(2 * 100px + 10px);
        justify-content: center;
        align-items: center;
    }

    .suspect-container {
        display: flex;
        flex-direction: column;
        text-align: center;
        justify-items: end;
        gap: 10px;
        width: 200px;
        margin-left: 40px;
    }

    .image-parent {
        display: flex;
        width: 100%;
    }

    .image-parent img {
        width: 100%;
        height: 700px;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }

    .header img {
        width: auto;
        height: 100%;
        image-rendering: pixelated;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content-parent {
        display: flex;
        position: absolute;
        bottom: 15px;
        left: 15px;
        width: calc(100% - 30px);
        align-items: end;
        gap: 15px;
    }

    .content {
        flex-grow: 1;
        overflow: auto;
        display: flex;
        flex-direction: column;
        gap: 4px;
        max-width: 700px;
    }

    .content p {
        margin: 0px;
        width: fit-content;
        background-color: #000000c3;
        border-radius: 20px;
        padding: 4px 10px;
    }

    .map-button {
        background-color: #000000c3;
        height: 100px;
        width: 100px;
        border-radius: 50%;
        border: none;
        margin-left: auto;
        padding: 10px;
    }

    .map-button:hover {
        cursor: pointer;
    }

    .map-button .map-img {
        width: 100%;
        height: 100%;
        border-radius: 50%;  /* Zorg ervoor dat de afbeelding ook een cirkel wordt */
        object-fit: cover;  /* Zorg ervoor dat de afbeelding goed binnen de cirkel wordt geschaald */
    }

    .toggle-sound-button {
        position: absolute;
        top: 5px;
        right: 5px;
        color: white;
        background: transparent;
        border: none;
        padding-right: 6px;
        padding-left: 6px;
        cursor: pointer;
    }

    .footer {
        border-radius: 10px;
        background-color: transparent;
        margin-top: 10px;
        display: flex;
        height: fit-content;
        width: 900px;
        display: flex;
    }

    .footer .action-options {
        display: flex;
        gap: 8px;
        height: fit-content;
        flex-wrap: wrap;
    }

    .footer .game-object-options {
        display: flex;
    }

    .footer .game-object-options .options-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: auto;
        border-right: solid 2px #ffffff99;
        margin-right: 20px;
        padding-right: 20px;
    }

    .footer .game-object-options .options-header span {
        opacity: 70%;
        font-style: italic;
    }

    .footer .game-object-options .options {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .footer .button {
        background-color: #4b628b81;
        border-radius: 10px;
        padding: 5px 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    .footer .button svg {
        display: flex;
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: #332c57;
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    private _actionButtonPressed: boolean = false;

    private _toggleSoundButton: HTMLButtonElement | null = null;
    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderMainScreen()}
            ${this.renderFooter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);

        const mapButton: HTMLButtonElement | null = this.shadowRoot.querySelector("#mapButton");
        if (mapButton) {
            mapButton.addEventListener("click", () => this.openMapComponent());
        }

        this._toggleSoundButton = this.shadowRoot.querySelector("#toggle_sound_button");

        if (this._toggleSoundButton) {
            this._toggleSoundButton.addEventListener("click", () => this.toggleSound());
        }
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderMainScreen(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
            <div class="main-screen-parent">
                ${this.renderInventory()}
                <div class="main-screen">
                    ${this.renderTitle()}
                    <div class="image-parent">
                        ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                        ${this._currentGameState?.avatar ? `<img class="character-img" src="/assets/img/characters/${this._currentGameState.avatar}.png">` : ""}
                    </div>
                    ${this.renderContent()}
                    ${this.renderToggleSoundButton()}
                </div>
                ${this.renderJournal()}
            </div>
            `;
        }

        return "Image not found";
    }

    private renderInventory(): string {
        if (this._currentGameState?.roomAlias !== "startup" && this._currentGameState?.roomAlias !== "final-room") {
            return `
            <div class="inventory">
                Items
                <inventory-items>
                </inventory-items>
            </div>
            `;
        } else {
            return `
                <div class="inventory"></div>
            `;
        }
    }

    private renderJournal(): string {
        if (this._currentGameState?.roomAlias !== "startup" && this._currentGameState?.roomAlias !== "final-room" && this._currentGameState?.baseItems.hasObtainedSuspectNote) {
            return `
                <div class="suspect-container">
                    Suspects
                    <suspect-list>
                    </suspect-list>
                </div>
            `;
        } else {
            return `
                <div class="poopy-list"></div>
            `;
        }
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content-parent">
                <div class="content">
                    ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
                </div>
                ${this._currentGameState?.baseItems.hasObtainedMap && this._currentGameState.roomAlias !== "final-room" ? this.renderMapButton() : ""}
            </div>
        `;
    }

    private renderToggleSoundButton(): string {
        const audio: HTMLAudioElement = document.querySelector("#background-music") as HTMLAudioElement;
        const musicIsOn: boolean = !audio.paused ? true : false;

        if (this._currentGameState?.roomAlias === "startup") {
            audio.src = "/assets/sound/startscreen-music.mp3";
            audio.volume = 0.1;
        }
        else {
            audio.src = "/assets/sound/background-music.mp3";
            audio.volume = 1.0;
            if (musicIsOn) {
                audio.play().then(() => {
                    this._toggleSoundButton!.innerHTML = this.getSoundIcon(true);
                }).catch((error: unknown) => {
                    console.error("Error playing audio:", error);
                });
            }
        };

        return `
            <button id="toggle_sound_button" class="toggle-sound-button">
                ${this.getSoundIcon(musicIsOn)}
            </button>
        `;
    }

    private getSoundIcon(isPlaying: boolean | undefined): string {
        return isPlaying
            ? `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-volume2-icon lucide-volume-2">
                <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"/>
                <path d="M16 9a5 5 0 0 1 0 6"/>
                <path d="M19.364 18.364a9 9 0 0 0 0-12.728"/>
            </svg>`
            : `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-volume-off-icon lucide-volume-off">
                <path d="M16 9a5 5 0 0 1 .95 2.293"/><path d="M19.364 5.636a9 9 0 0 1 1.889 9.96"/>
                <path d="m2 2 20 20"/>
                <path d="m7 7-.587.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298V11"/>
                <path d="M9.828 4.172A.686.686 0 0 1 11 4.657v.686"/>
            </svg>`;
    }

    private toggleSound(): void {
        const audio: HTMLAudioElement = document.querySelector("#background-music") as HTMLAudioElement;

        if (!this._toggleSoundButton) {
            console.error("Toggle sound button not found.");
            return;
        }

        if (audio.paused) {
            audio.play().then(() => {
                this._toggleSoundButton!.innerHTML = this.getSoundIcon(true);
            }).catch((error: unknown) => {
                console.error("Error playing audio:", error);
            });
        } else {
            audio.pause();
            this._toggleSoundButton.innerHTML = this.getSoundIcon(false);
        }
    }

    private renderMapButton(): string {
        const htmlString: string = `
            <button id="mapButton" class="map-button">
                    <img src="/assets/img/icons/map-icon.png" class="map-img"/>
            </button>
        `;

        return htmlString;
    }

    private openMapComponent(): void {
        if (!this.shadowRoot) return;

        const existingMap: HTMLElement | null = this.shadowRoot.querySelector("map-component");
        if (!existingMap) {
            const mapComponent: HTMLElement = document.createElement("map-component");
            this.shadowRoot.appendChild(mapComponent);
        }
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
            <div class="footer">
                ${!this._actionButtonPressed
                ? html`
                        <div class="action-options">
                            ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                        </div>
                    `
                : ""
            }
                ${this._actionButtonPressed
                ? html`
                        <div class="game-object-options">
                            <div class="options-header">
                                ${this.renderGoBackButton()}
                                <span>${this._selectedActionButton?.name}</span>
                            </div>
                            <div class="options">
                                ${this._selectedActionButton
                        ? this._currentGameState?.objects.filter(object => object.belongsToAction.includes(this._selectedActionButton?.alias ?? "")).map(button => this.renderGameObjectButton(button)) || ""
                        : ""
                    }
                            </div>
                        </div>
                    `
                : ""
            }
            </div>
        `;
    }

    private renderGoBackButton(): HTMLElement {
        const goBackElement: HTMLElement = html`
            <a class="button">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-left-from-line">
                    <path d="m9 6-6 6 6 6"/><path d="M3 12h14"/>
                    <path d="M21 19V5"/>
                </svg>
            </a>
        `;

        goBackElement.addEventListener("click", () => this.handleClickGoBack());

        return goBackElement;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    private handleClickGoBack(): void {
        this._actionButtonPressed = false;

        this.render();
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();
            this._actionButtonPressed = true;

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        this._actionButtonPressed = false;

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
